import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Download, ExternalLink, FileText, Send } from 'lucide-react'
import { useRef, useState } from 'react'

import { LoadingButton } from '@/components/loading-button'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { copyText, formatMoney } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { NewInvoiceDialog } from '@/features/receivables/new-invoice-dialog'
import { RefundDialog } from '@/features/receivables/refund-dialog'

import { useQuoteBuilderApi } from '../api/client'
import { quoteQueryOptions, workflowQueryOptions } from '../api/queries'
import { numberValue } from '../ui/numbers'
import { ErrorState, LoadingState, SurfaceCard } from '../ui/page'
import type { WorkflowTabProps } from '../workflow/types'
import {
  financingQueryOptions,
  invoiceDocumentHref,
  invoiceErrorMessage,
  invoicingApi,
  invoicingQueryKey,
  type Invoice,
  type InvoicingState,
  type NewPayment,
} from './api'
import { finalQuoteProblem } from './final-quote'
import { PaymentsCard } from './payments-card'

type Action = 'create' | 'send' | 'refresh' | 'create-balance' | 'send-balance'

const dateTime = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** Where the customer pays: open it as they see it, or copy the pay link to send yourself. */
export function PaymentLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex flex-wrap gap-2">
      <Button asChild className="flex-1" size="sm" variant="outline">
        {/* eslint-disable-next-line no-restricted-syntax -- the customer's invoice page */}
        <a href={url} rel="noopener noreferrer" target="_blank">
          <ExternalLink className="size-4" />
          View as customer
        </a>
      </Button>
      <Button
        className="flex-1"
        onClick={(event) => {
          void copyText(url, event.currentTarget).then((done) => {
            if (!done) return
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
          })
        }}
        size="sm"
        type="button"
        variant="outline"
      >
        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        {copied ? 'Copied' : 'Copy pay link'}
      </Button>
    </div>
  )
}

function statusLabel(invoice: Invoice) {
  if (invoice.status === 'paid') return { label: 'Paid', tone: 'success' as const }
  if (invoice.status === 'void') return { label: 'Void', tone: 'neutral' as const }
  if (invoice.status === 'uncollectible') return { label: 'Uncollectible', tone: 'danger' as const }
  if (!invoice.finalized_at) return { label: 'Pending', tone: 'neutral' as const }
  if (invoice.payment_processing_at) return { label: 'Bank payment processing', tone: 'progress' as const }
  return { label: 'Awaiting payment', tone: 'warning' as const }
}

/** The invoice card (demo `InvoicePanel`). */
export function InvoicePanel({
  state,
  balance = false,
  activeAction,
  onCreate,
  onSend,
  disabled = false,
  needsFinalQuote = false,
}: {
  state: InvoicingState
  /** The balance invoice (the 50/50 second half) instead of the first installment. */
  balance?: boolean
  activeAction: Action | null
  onCreate: () => void
  onSend: () => void
  disabled?: boolean
  /** The final quote the first invoice bills is not current (USA-1336): Start's Final quote step publishes it. */
  needsFinalQuote?: boolean
}) {
  const invoice = balance ? state.balance_invoice : state.invoice
  const title = balance ? 'Balance invoice' : 'Invoice'
  const creating = activeAction === (balance ? 'create-balance' : 'create')
  const sending = activeAction === (balance ? 'send-balance' : 'send')
  const busy = disabled || activeAction !== null
  if (!invoice && balance) {
    return (
      <SurfaceCard title={title}>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="text-lg font-semibold tabular-nums">{formatMoney(state.invoice?.snapshot.future_balance_amount)}</p>
            <p className="text-xs text-muted-foreground">Remaining balance</p>
          </div>
          <LoadingButton disabled={busy} loading={creating} loadingText="Creating..." onClick={onCreate} size="sm" type="button">
            <FileText className="size-4" />
            Create balance invoice
          </LoadingButton>
        </div>
      </SurfaceCard>
    )
  }
  if (!invoice && needsFinalQuote) {
    // Said before any click: no Create invoice that would be refused (USA-1336).
    return (
      <SurfaceCard title={title}>
        <div className="grid min-h-52 place-items-center gap-3 p-6 text-center" data-invoice-needs-final-quote>
          <div>
            <FileText aria-hidden="true" className="mx-auto size-7 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-semibold">No invoice yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Publish the final quote first; you can send the invoice with it.
            </p>
          </div>
        </div>
      </SurfaceCard>
    )
  }
  if (!invoice) {
    return (
      <SurfaceCard title={title}>
        <div className="grid min-h-52 place-items-center gap-3 p-6 text-center">
          <div>
            <FileText aria-hidden="true" className="mx-auto size-7 text-muted-foreground" />
            <h3 className="mt-3 text-sm font-semibold">Ready to invoice</h3>
          </div>
          <LoadingButton disabled={busy} loading={creating} loadingText="Creating..." onClick={onCreate} type="button">
            <FileText className="size-4" />
            Create invoice
          </LoadingButton>
        </div>
      </SurfaceCard>
    )
  }
  const status = statusLabel(invoice)
  const stale = invoice.source_revision !== state.quote_revision
  return (
    <SurfaceCard title={title}>
      <div className="grid gap-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-2xl font-bold tracking-tight tabular-nums">{formatMoney(invoice.installment_amount)}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {numberValue(invoice.snapshot.installment_percent)}% of {formatMoney(invoice.quote_amount)}
            </p>
          </div>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>
        <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
          <span>{invoice.number ? `Invoice ${invoice.number}` : 'Invoice'}</span>
          <span>Revision {invoice.source_revision}</span>
        </div>
        <dl className="grid grid-cols-3 gap-3 text-sm tabular-nums">
          <div>
            <dt className="text-xs text-muted-foreground">Due</dt>
            <dd className="mt-1 font-semibold">{formatMoney(invoice.amount_due)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Paid</dt>
            <dd className="mt-1 font-semibold">{formatMoney(invoice.amount_paid)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Remaining</dt>
            <dd className="mt-1 font-semibold">{formatMoney(invoice.amount_remaining)}</dd>
          </div>
        </dl>
        {invoice.paid_at ? (
          <p className="text-xs text-muted-foreground">
            Paid {dateTime.format(new Date(invoice.paid_at))}
          </p>
        ) : invoice.sent_at ? (
          <p className="text-xs text-muted-foreground">
            Last sent {dateTime.format(new Date(invoice.sent_at))} to {invoice.recipient_email}
          </p>
        ) : invoice.due_at && invoice.status === 'open' ? (
          <p className="text-xs text-muted-foreground">Due {day.format(new Date(invoice.due_at))}</p>
        ) : null}
        {stale || invoice.acceptance_status === 'superseded' ? (
          <p className="text-sm text-amber-800 dark:text-amber-200" role="status">
            This {invoice.paid_at ? 'payment' : 'invoice'} covers an earlier revision. Create a new quote for revised totals.
          </p>
        ) : invoice.acceptance_status === 'decision_recorded' ? (
          <p className="text-sm text-amber-800 dark:text-amber-200" role="status">
            Payment received. Review the customer's response before proceeding.
          </p>
        ) : null}
        {invoice.pay_url && invoice.status !== 'void' ? <PaymentLink url={invoice.pay_url} /> : null}
        {invoice.pdf_url ? (
          <Button asChild size="sm" variant="outline">
            {/* eslint-disable-next-line no-restricted-syntax -- the invoice PDF */}
            <a href={invoiceDocumentHref(invoice.pdf_url)} rel="noopener noreferrer" target="_blank">
              <Download className="size-4" />
              Download invoice
            </a>
          </Button>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {!invoice.finalized_at ? (
            <LoadingButton className="flex-1" disabled={busy || stale} loading={creating} loadingText="Creating..." onClick={onCreate} size="sm" type="button">
              <FileText className="size-4" />
              Retry invoice
            </LoadingButton>
          ) : invoice.status === 'open' ? (
            <LoadingButton className="flex-1" disabled={busy || stale} loading={sending} loadingText="Sending..." onClick={onSend} size="sm" type="button">
              <Send className="size-4" />
              {invoice.sent_at ? 'Resend invoice' : 'Send invoice'}
            </LoadingButton>
          ) : null}
        </div>
      </div>
    </SurfaceCard>
  )
}

/** Extra items or updated pricing billed after the quote, as many times as needed (USA-378). */
function AdditionalInvoices({ quoteId, invoices }: { quoteId: string; invoices: Invoice[] }) {
  return (
    <SurfaceCard
      actions={
        <>
          <RefundDialog source={{ quoteId }} />
          <NewInvoiceDialog source={{ quoteId }} />
        </>
      }
      title="Additional invoices"
    >
      {invoices.length ? (
        <ul className="divide-y">
          {invoices.map((invoice) => {
            const status = statusLabel(invoice)
            return (
              <li className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" key={invoice.id}>
                <span className="min-w-0">
                  <Link
                    className="block truncate font-medium hover:underline"
                    params={{ invoiceId: invoice.id }}
                    to="/receivables/$invoiceId"
                  >
                    {invoice.stripe_invoice_number ? `Invoice ${invoice.stripe_invoice_number}` : 'Invoice'}
                  </Link>
                  <span className="block truncate text-xs text-muted-foreground">
                    {invoice.snapshot.items[0]?.description}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="font-medium tabular-nums">{formatMoney(invoice.amount_due)}</span>
                  <StatusPill tone={status.tone}>{status.label}</StatusPill>
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}
    </SurfaceCard>
  )
}

/** The Invoicing stage, ported from the demo's `InvoicingTab`. */
export function InvoicingTab({ active = true, quoteId, quote, onQuoteChange, onWorkflowChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const invoices = invoicingApi(api)
  const queryClient = useQueryClient()
  const lock = useRef(false)
  const sendRequest = useRef<string | null>(null)
  const latestRevision = useRef(quote.revision)
  latestRevision.current = Math.max(latestRevision.current, quote.revision)
  const [activeAction, setActiveAction] = useState<Action | null>(null)
  const [refreshNeeded, setRefreshNeeded] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [statusError, setStatusError] = useState<unknown>(null)
  const keepNewer = (cached: InvoicingState | undefined, next: InvoicingState) =>
    cached && cached.quote_revision > next.quote_revision ? cached : next
  const financing = useQuery({ ...financingQueryOptions(api, quoteId), enabled: active })
  const query = useQuery<InvoicingState>({
    enabled: active,
    queryKey: invoicingQueryKey(quoteId),
    // While an invoice awaits payment, check for the payment every minute.
    refetchInterval: (current) => {
      const data = current.state.data
      const open = [data?.invoice, data?.balance_invoice].some(
        (item) => item?.finalized_at && item.status === 'open',
      )
      return open ? 60_000 : false
    },
    queryFn: async ({ signal }) => {
      let state = await invoices.read(quoteId, signal)
      // Each read checks the payment service so a payment made moments ago shows at once.
      if (state.invoice?.stripe_invoice_id) {
        try {
          state = await invoices.refresh(quoteId, latestRevision.current, signal)
          setStatusError(null)
        } catch (error) {
          if (signal.aborted) throw error
          setStatusError(error)
        }
      }
      return keepNewer(queryClient.getQueryData<InvoicingState>(invoicingQueryKey(quoteId)), state)
    },
  })

  async function refresh() {
    setRefreshing(true)
    const [nextQuote, nextWorkflow, nextInvoice] = await Promise.allSettled([
      queryClient.fetchQuery({ ...quoteQueryOptions(api, quoteId), staleTime: 0 }),
      queryClient.fetchQuery({ ...workflowQueryOptions(api, quoteId), staleTime: 0 }),
      invoices.read(quoteId),
    ])
    if (nextQuote.status === 'fulfilled' && nextQuote.value.revision >= latestRevision.current) {
      latestRevision.current = nextQuote.value.revision
      onQuoteChange(nextQuote.value)
    }
    if (nextWorkflow.status === 'fulfilled' && nextWorkflow.value.revision >= latestRevision.current) {
      onWorkflowChange(nextWorkflow.value)
    }
    if (nextInvoice.status === 'fulfilled') {
      queryClient.setQueryData<InvoicingState>(invoicingQueryKey(quoteId), (cached) => keepNewer(cached, nextInvoice.value))
    }
    const recovered =
      nextQuote.status === 'fulfilled' &&
      nextWorkflow.status === 'fulfilled' &&
      nextInvoice.status === 'fulfilled' &&
      nextQuote.value.revision >= latestRevision.current &&
      nextWorkflow.value.revision >= latestRevision.current &&
      nextInvoice.value.quote_revision >= latestRevision.current
    setRefreshNeeded(!recovered)
    setRefreshing(false)
  }

  const mutation = useMutation({
    retry: false,
    mutationFn: (action: Action) =>
      action === 'create'
        ? invoices.create(quoteId, latestRevision.current)
        : action === 'create-balance'
          ? invoices.createBalance(quoteId, latestRevision.current)
          : action === 'send' || action === 'send-balance'
            ? invoices.send(
                quoteId,
                latestRevision.current,
                sendRequest.current ?? crypto.randomUUID(),
                action === 'send-balance' ? 2 : 1,
              )
            : invoices.refresh(quoteId, latestRevision.current),
    onSuccess: async (state, action) => {
      if (action === 'send' || action === 'send-balance') sendRequest.current = null
      if (action === 'refresh') setStatusError(null)
      latestRevision.current = Math.max(latestRevision.current, state.quote_revision)
      queryClient.setQueryData<InvoicingState>(invoicingQueryKey(quoteId), (cached) => keepNewer(cached, state))
      void queryClient.invalidateQueries({ queryKey: ['quote-financing', quoteId] })
      await refresh()
    },
    // A lost response can still mean success. Recover with reads, never an automatic write.
    onError: async () => {
      await refresh()
    },
    onSettled: () => {
      lock.current = false
      setActiveAction(null)
    },
  })

  const paymentRequest = useRef<string | null>(null)
  const recordPayment = useMutation({
    retry: false,
    mutationFn: (payment: Omit<NewPayment, 'request_id'>) =>
      invoices.recordPayment(quoteId, { ...payment, request_id: (paymentRequest.current ??= crypto.randomUUID()) }),
    onSuccess: async (state) => {
      paymentRequest.current = null
      queryClient.setQueryData<InvoicingState>(invoicingQueryKey(quoteId), (cached) => keepNewer(cached, state))
      await refresh()
    },
    onError: async () => {
      await refresh()
    },
    onSettled: () => {
      lock.current = false
    },
  })
  const removePayment = useMutation({
    retry: false,
    mutationFn: (paymentId: string) => invoices.removePayment(quoteId, paymentId),
    onSuccess: async (state) => {
      queryClient.setQueryData<InvoicingState>(invoicingQueryKey(quoteId), (cached) => keepNewer(cached, state))
      await refresh()
    },
    onSettled: () => {
      lock.current = false
    },
  })
  const paymentBusy = recordPayment.isPending || removePayment.isPending

  const run = (action: Action) => {
    if (lock.current || refreshNeeded || refreshing || paymentBusy) return
    lock.current = true
    if (action === 'send' || action === 'send-balance') sendRequest.current ??= crypto.randomUUID()
    setActiveAction(action)
    mutation.mutate(action)
  }

  if (query.isLoading) return <LoadingState label="Loading invoice" />
  if (financing.data?.financing?.term_type === 'prepaid') {
    const paid = financing.data.financing
    return (
      <SurfaceCard className="w-full max-w-md" title="Invoice">
        <div className="grid gap-2 p-4 text-sm">
          <p className="flex items-center gap-2 font-semibold">
            <Check aria-hidden="true" className="size-4 text-emerald-600" />
            Paid separately (prepaid)
          </p>
          <p className="text-muted-foreground">No invoice is sent for prepaid terms.</p>
          {paid.prepaid_reference ? (
            <p>
              <span className="text-muted-foreground">Reference </span>
              {paid.prepaid_reference}
            </p>
          ) : null}
          {paid.prepaid_note ? <p className="whitespace-pre-line">{paid.prepaid_note}</p> : null}
        </div>
      </SurfaceCard>
    )
  }
  if (!query.data) {
    return (
      <ErrorState
        title="Invoice unavailable"
        description="Payment status could not be loaded. Reload the quote and try again."
        onRetry={() => void query.refetch()}
      />
    )
  }
  const state = query.data
  // A payment recorded without an invoice covers what a new Stripe invoice would bill.
  const paidByHand = (state.payments ?? []).some((item) => item.entry_type === 'external_payment' && !item.removed_at)
  const owed = numberValue(state.owed ?? 0)
  const blocked = refreshNeeded || refreshing || paymentBusy
  const busy = blocked || activeAction !== null
  return (
    <div className="grid w-full max-w-md gap-3">
      {state.invoice || (!paidByHand && owed > 0) ? (
        <InvoicePanel
          activeAction={activeAction}
          disabled={blocked}
          onCreate={() => run('create')}
          onSend={() => run('send')}
          needsFinalQuote={finalQuoteProblem(state.final_quote) !== null}
          state={state}
        />
      ) : null}
      {state.invoice?.finalized_at &&
      numberValue(state.invoice.snapshot.future_balance_amount) > 0 &&
      (state.balance_invoice || (!paidByHand && owed > 0)) ? (
        <InvoicePanel
          activeAction={activeAction}
          balance
          disabled={blocked}
          onCreate={() => run('create-balance')}
          onSend={() => run('send-balance')}
          state={state}
        />
      ) : null}
      {state.invoice?.finalized_at || paidByHand ? (
        <AdditionalInvoices invoices={state.additional_invoices ?? []} quoteId={quoteId} />
      ) : null}
      <PaymentsCard
        busy={busy}
        onRecord={(payment, done) => {
          if (lock.current || busy) return
          lock.current = true
          recordPayment.mutate(payment, { onSuccess: done })
        }}
        onRemove={(paymentId) => {
          if (lock.current || busy) return
          lock.current = true
          removePayment.mutate(paymentId)
        }}
        recording={recordPayment.isPending}
        removingId={removePayment.isPending ? (removePayment.variables ?? null) : null}
        state={state}
      />
      {refreshNeeded || query.isError ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-3 text-sm" role="status">
          <span>{mutation.isSuccess ? 'Invoice updated. Refresh to load the latest quote.' : 'Refresh to load the latest quote before continuing.'}</span>
          <Button disabled={refreshing} onClick={() => void refresh()} size="sm" type="button" variant="outline">
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      ) : null}
      {mutation.isError || recordPayment.isError || removePayment.isError || statusError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive" role="alert">
          {invoiceErrorMessage(mutation.error || recordPayment.error || removePayment.error || statusError)}
        </p>
      ) : null}
    </div>
  )
}
